import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { formatDate } from "../dashboardHelpers";
import { VOUCHER_PILL_COLOR } from "../dashboardConstants";
import RowShell from "./RowShell";

function VoucherRow({ voucher, currency, onPayNow, payLoadingId }) {
  const cur = voucher.currency || currency || "";
  const remain = Number.isFinite(voucher.remainValue) ? voucher.remainValue : parseFloat(voucher.remainValue) || 0;
  const initial = Number.isFinite(voucher.initialValue) ? voucher.initialValue : parseFloat(voucher.initialValue) || 0;
  const isActive = voucher.status === "active";
  const isPending = voucher.status === "pending";
  const statusLabel = isPending ? "pending payment" : voucher.status;
  const statusColor = VOUCHER_PILL_COLOR[voucher.status] || "default";
  const hasPayAction = isPending && !!voucher.invoiceId && !!onPayNow;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Same treatment as the package row's Package/Membership chip, so the two
  // lists in this tab label their rows the same way.
  const kindChip = (
    <Chip
      size="small"
      label="Voucher"
      variant="outlined"
      sx={{
        flexShrink: 0,
        height: isMobile ? 22 : 18,
        fontSize: isMobile ? "0.7rem" : "0.65rem",
        fontWeight: 600,
        letterSpacing: 0.3,
        color: "primary.main",
        borderColor: "primary.main"
      }}
    />
  );

  return (
    <RowShell stack>
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="subtitle2"
            sx={{ minWidth: 0, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: { xs: 2, sm: 1 }, overflow: "hidden" }}
          >
            {voucher.name}
          </Typography>
          {/* On a phone both chips go to the action line instead, so the name keeps the full width. */}
          {!isMobile && kindChip}
        </Box>
        <Typography variant="body2" sx={{ color: isActive ? "primary.main" : "text.secondary", fontWeight: 500 }}>
          Balance: {cur} {remain.toFixed(2)}
          {initial > 0 ? ` / ${cur} ${initial.toFixed(2)}` : ""}
          {" — "}
          {voucher.noExpiry ? "No expiry" : `Expires ${formatDate(voucher.expiryDate)}`}
        </Typography>
        {isPending && (
          <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.25 }}>
            Voucher is awaiting payment and can't be used until the purchase invoice is paid.
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          flexShrink: 0,
          justifyContent: { xs: "flex-start", sm: "flex-end" }
        }}
      >
        {isMobile && kindChip}
        <Chip
          size="small"
          label={statusLabel}
          color={statusColor}
          variant="outlined"
          sx={{ height: "1.375rem", fontSize: "0.7rem", fontWeight: 500, letterSpacing: 0.3 }}
        />
        {hasPayAction && (
          <Button
            size="small"
            variant="contained"
            disabled={payLoadingId === voucher._id}
            onClick={() => onPayNow(voucher.invoiceId, voucher._id)}
            sx={{
              // Leads the action line so the button never sits behind the chips.
              order: -1,
              height: "1.75rem",
              px: 1.5,
              flexShrink: 0,
              textTransform: "none",
              color: "common.white",
              boxShadow: "none"
            }}
          >
            {payLoadingId === voucher._id ? <CircularProgress size={16} /> : "Pay Now"}
          </Button>
        )}
      </Box>
    </RowShell>
  );
}

export default VoucherRow;
